import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useParams } from "react-router-dom";

import { AppShell } from "./components/AppShell";
import { Container } from "./components/Container";
import { LoadingModal } from "./components/LoadingModal";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SaveErrorNotice } from "./components/SaveErrorNotice";
import ScrollTop from "./components/ScrollTop";
import { LegacySiteRedirect, SiteRoute } from "./components/SiteRoute";
import { TailwindReset } from "./components/TailwindReset";
import { AppContextProvider, AppState, defaultAppState } from "./contexts/App";
import { clearAdIntent, readAdIntent, saveAdIntent } from "./hooks/AdIntent";
import { forgetEditingSite, readEditingSite, rememberEditingSite, restoreEditingSite } from "./hooks/EditingSite";
import { usePosLoginContinue } from "./hooks/PosLogin";
import { useQueryUrl } from "./hooks/QueryUrl";
import { LEGACY_SITE_ROUTES } from "./hooks/SitePath";
import { useSiteRef } from "./hooks/SiteRef";
import { Site } from "./model/site";
import { AccountPage } from "./pages/Account";
import { AccountDeletePage } from "./pages/Account/Delete";
import AdSignupPage from "./pages/AdSignup";
import { adNicheBySlug } from "./pages/AdSignup/niches";
import { startFirstKeepo } from "./pages/AdSignup/startFirstKeepo";
import BusinessCardPage from "./pages/BusinessCard";
import ContactsPage from "./pages/Contacts";
import ContactDetailPage from "./pages/Contacts/Detail";
import DomainsPage from "./pages/Domains";
import DomainCheckoutPage from "./pages/Domains/Checkout";
import DomainDetailPage from "./pages/Domains/Detail";
import MyDomainsPage from "./pages/Domains/Mine";
import EditorPage from "./pages/Editor";
import { FeedbackPage } from "./pages/Feedback";
import { HelpPage } from "./pages/Help";
import KeepoDashboardPage from "./pages/KeepoDashboard";
import KeepoPageFormPage from "./pages/KeepoPages/PageForm";
import { LegalTermsPage } from "./pages/LegalTerms";
import LinksPage from "./pages/Links";
import LinkFormPage from "./pages/Links/Form";
import LinkViewPage from "./pages/Links/View";
import LoginPage from "./pages/Login";
import PlusBenefitsPage from "./pages/PlusBenefits";
import RecoverPasswordPage from "./pages/RecoverPassword";
import ReleasesPage from "./pages/Releases";
import SeoPage from "./pages/Seo";
import SettingsPage from "./pages/Settings";
import { CustomDomainPage } from "./pages/Settings/pages/CustomDomain";
import { FacebookPixelPage } from "./pages/Settings/pages/FacebookPixel";
import { GoogleAnalytics } from "./pages/Settings/pages/GoogleAnalytics";
import { SiteAgeGatePage } from "./pages/Settings/pages/SiteAgeGate";
import { SitePasswordPage } from "./pages/Settings/pages/SitePassword";
import { TagManager } from "./pages/Settings/pages/TagManager";
import SitesPage from "./pages/Sites";
import StatisticsPage from "./pages/Statistics";
import { SubscriptionPage } from "./pages/Subscription";
import { keepoInternalAnalyticsService } from "./services/analytics";
import { api } from "./services/api";
import * as authenticationService from "./services/auth";
import { applyAccountLanguage } from "./services/i18ln/accountLanguage";
import { captureTrafficOriginFromCookie, captureTrafficOriginFromUrl } from "./services/trafficOrigin";

// The screens of the Keepo being edited: after a reload, they reopen it instead of going to the list
const SITE_ROUTES = /^\/(keepo|editor|seo|analytics|contacts|settings)(\/|$)/;

type MainAuthenticatedProps = {
  /** Reopens the Keepo that was being edited in this tab (a reload on one of its screens). */
  onRestore: (site: Site, appState: Partial<AppState>) => void;
};

export const MainAuthenticatedComponent = ({ onRestore }: MainAuthenticatedProps) => {
  const [nextPage, setNextPage] = useState<string>();
  const { getPageAndClear } = usePosLoginContinue();
  const { pathname } = useLocation();

  // `isCurrent`: false once this arrival was dropped (e.g. run twice in development): it then decides nothing,
  // or it would open a second new Keepo while going to the first one's address
  const loadUser = useCallback(
    async (isCurrent: () => boolean) => {
      const [user, sites] = await Promise.all([api.getUser(), api.getSites()]);
      if (!isCurrent()) {
        return;
      }

      // the ad page the person came from (if any): goes with the sign up event
      const adIntent = readAdIntent();
      keepoInternalAnalyticsService.notifyLoginEvent(
        user,
        adIntent ? { niche: adIntent.slug ?? "generica" } : undefined
      );
      const posLoginPage = getPageAndClear();

      const editing = !posLoginPage && SITE_ROUTES.test(pathname) ? readEditingSite() : undefined;
      const restored = editing && restoreEditingSite(sites, editing);
      if (restored) {
        // the Keepo's screens come back with it: this same address opens them
        onRestore(restored.site, restored.appState);
        return;
      }
      if (editing) {
        forgetEditingSite();
      }

      if (posLoginPage) {
        setNextPage(posLoginPage);
        return;
      }
      // From an ad page: an account with no Keepo goes straight to the name screen; the intent is used once
      if (adIntent) {
        if (sites.length === 0) {
          setNextPage(startFirstKeepo(adIntent, { onStart: onRestore }));
          return;
        }
        clearAdIntent();
      }
      // No Keepo yet (a new account, or one whose Keepos were all deleted): straight to the name screen of a new one
      if (sites.length === 0) {
        setNextPage(startFirstKeepo(null, { onStart: onRestore }));
        return;
      }

      setNextPage("/sites");
    },
    // once, on arrival: the address is the one the reload was on
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getPageAndClear]
  );

  useEffect(() => {
    let current = true;
    loadUser(() => current);
    return () => {
      current = false;
    };
  }, [loadUser]);

  return !nextPage ? <LoadingModal /> : <Navigate to={nextPage} replace />;
};

// /comecar when already signed in: no sign up to do, so it goes on as after one (the shortcut, or the list)
export const AdSignupRoute = ({ authenticated, onRestore }: { authenticated: boolean } & MainAuthenticatedProps) => {
  const { nicho } = useParams();
  // only who was already signed in on arrival: whoever signs up here goes on through "/" (see Register)
  const [wasAuthenticated] = useState(authenticated);
  // saved in an effect (not while rendering); the arrival reads it only after its requests, so it is there by then
  useEffect(() => {
    if (wasAuthenticated) saveAdIntent(adNicheBySlug(nicho).slug);
  }, [wasAuthenticated, nicho]);

  return wasAuthenticated ? <MainAuthenticatedComponent onRestore={onRestore} /> : <AdSignupPage />;
};

type MainComponentProps = {
  authenticated: boolean;
} & MainAuthenticatedProps;

const MainComponent = ({ authenticated, onRestore }: MainComponentProps) => {
  const action = (useQueryUrl().get("action") || "start").toLowerCase();
  const plusPlan = useQueryUrl().get("plan");
  const detailSite = useQueryUrl().get("site");
  const { setPage } = usePosLoginContinue();

  if (action == "plus") {
    // For a given Keepo (e.g. subscribing again after a lapsed PLUS): the PLUS screen goes on to its checkout
    if (detailSite) {
      setPage(`/subscription/features?site=${encodeURIComponent(detailSite)}`);
    } else if (plusPlan && ["ANNUALLY", "MONTHLY"].includes(plusPlan.toUpperCase())) {
      setPage(`/subscription/selection?plan=${plusPlan.toUpperCase()}`);
    } else {
      setPage(`/subscription/features`);
    }
  }

  if (action == "detail" && detailSite) {
    setPage(`/subscription/detail?site=${encodeURIComponent(detailSite)}`);
  }

  // The renewal emails of a PLUS paid once with Pix or boleto
  if (action == "renew" && detailSite) {
    setPage(`/subscription/renew?site=${encodeURIComponent(detailSite)}`);
  }

  return authenticated ? <MainAuthenticatedComponent onRestore={onRestore} /> : <LoginPage />;
};

function App() {
  const { i18n } = useTranslation();
  const { getRefAndClear } = useSiteRef();
  // App renders the Router, so it can't use the router-based useQueryUrl hook
  const urlParams = new URLSearchParams(window.location.search);

  const [authenticated, setAuthenticated] = useState(false);
  const [loadingAuthenticatedState, setLoadingAuthenticationState] = useState(true);

  const [appState, setAppState] = useState<AppState>(defaultAppState);
  const [site, setSite] = useState<Site>();

  // The Keepo being edited is kept for this tab, so a reload reopens it (MainAuthenticatedComponent); forgotten
  // once nothing is being edited anymore (e.g. the Keepo was deleted)
  const wasEditing = useRef(false);
  useEffect(() => {
    if (appState.editingSiteId) {
      rememberEditingSite({ siteId: appState.editingSiteId, usingDraft: appState.editingSiteModified });
      wasEditing.current = true;
    } else if (wasEditing.current) {
      forgetEditingSite();
      wasEditing.current = false;
    }
  }, [appState.editingSiteId, appState.editingSiteModified]);

  const restoreSite = useCallback((restored: Site, editing: Partial<AppState>) => {
    setAppState(current => ({ ...current, ...editing }));
    setSite(restored);
  }, []);

  // The account's language, applied once per load (this runs on every render, and the Account screen changes it):
  // whoever signs in reads the editor in the language they chose, whatever the browser in hand is in
  const accountLanguageApplied = useRef(false);
  const applyLanguageOf = useCallback(
    (user: { lang?: string }) => applyAccountLanguage(i18n, user.lang).catch(() => undefined),
    [i18n]
  );

  const markAuthenticated = useCallback(async () => {
    try {
      const user = await api.getInitialUser({
        defaultLang: i18n.language,
        ref: getRefAndClear(),
      });
      // a sign in or sign up just happened: the account's language applies now
      accountLanguageApplied.current = true;
      await applyLanguageOf(user);
      setAuthenticated(true);
    } catch {
      authenticationService.signOut();
    }
  }, [getRefAndClear, i18n.language, applyLanguageOf]);

  const handleAuthenticatedState = useCallback(async () => {
    try {
      const qp = "_token";
      const tokenFromUrl = urlParams.get(qp);
      if (tokenFromUrl) {
        authenticationService.storeLocalAuthToken(tokenFromUrl);

        // remove _token from URL so subsequent navigation doesn't expose it
        urlParams.delete(qp);
        const newSearch = urlParams.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
        window.history.replaceState(null, "", newUrl);
      }
    } catch (e) {
      // ignore URL parsing/storage errors and continue to auth check
    }

    const authenticated = await authenticationService.isAuthenticated();
    setAuthenticated(authenticated);
    if (authenticated) {
      try {
        const user = await api.getInitialUser({
          defaultLang: i18n.language,
          ref: getRefAndClear(),
        });
        if (!accountLanguageApplied.current) {
          accountLanguageApplied.current = true;
          await applyLanguageOf(user);
        }
      } catch {
        authenticationService.signOut();
      }
    }
    setLoadingAuthenticationState(false);
  }, [getRefAndClear, urlParams, i18n.language, applyLanguageOf]);

  // Once, on arrival: `handleAuthenticatedState` changes on every render (it depends on `urlParams`), and
  // capturing with it would record the landing link again right after the API got it
  useEffect(() => {
    captureTrafficOriginFromCookie();
    captureTrafficOriginFromUrl();
  }, []);

  useEffect(() => {
    handleAuthenticatedState();
  }, [handleAuthenticatedState]);

  return loadingAuthenticatedState ? (
    <LoadingModal />
  ) : (
    <AppContextProvider value={{ appState, setAppState, markAuthenticated }}>
      <Router>
        <ScrollTop />
        <AppShell authenticated={authenticated} site={site}>
          <Routes>
            <Route
              path="legal"
              element={
                <Container>
                  <LegalTermsPage />
                </Container>
              }
            />
            {/* the old first-steps screen: old links go through "/" */}
            <Route path="onboarding" element={<Navigate to="/" replace />} />

            <Route
              path="sites"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <SitesPage setSite={setSite} />
                </ProtectedRoute>
              }
            />

            <Route
              path="domains"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <DomainsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="business-card"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <BusinessCardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="links"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <LinksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="links/new"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <LinkFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="links/:id/edit"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <LinkFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="links/:id"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <LinkViewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="domains/mine"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <MyDomainsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="domains/checkout"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <DomainCheckoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="domains/:domain"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <DomainDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="account"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <AccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="account/delete"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <AccountDeletePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="feedback"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <FeedbackPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="novidades"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <ReleasesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="help/*"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <HelpPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="subscription/*"
              element={
                <ProtectedRoute authenticated={authenticated}>
                  <SubscriptionPage site={site} setSite={setSite} />
                </ProtectedRoute>
              }
            />
            <Route
              path="register/"
              element={
                <TailwindReset>
                  <AdSignupPage fromAd={false} />
                </TailwindReset>
              }
            />
            <Route
              path="comecar"
              element={
                <TailwindReset>
                  <AdSignupRoute authenticated={authenticated} onRestore={restoreSite} />
                </TailwindReset>
              }
            />
            <Route
              path="comecar/:nicho"
              element={
                <TailwindReset>
                  <AdSignupRoute authenticated={authenticated} onRestore={restoreSite} />
                </TailwindReset>
              }
            />
            <Route
              path="recovery-password/"
              element={
                <TailwindReset>
                  <RecoverPasswordPage />
                </TailwindReset>
              }
            />
            <Route
              path="recover-password/"
              element={
                <TailwindReset>
                  <RecoverPasswordPage />
                </TailwindReset>
              }
            />
            {/* A Keepo's screens, at its address: /sites/<short id>/settings... */}
            <Route
              path="sites/:siteId/*"
              element={
                <SiteRoute authenticated={authenticated} site={site} setSite={setSite}>
                  <Routes>
                    <Route
                      path=""
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <KeepoDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="plus"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <PlusBenefitsPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* the Keepo's pages are the editor's way in now: old links go there */}
                    <Route path="pages" element={<Navigate to="/editor/pages" replace />} />
                    <Route
                      path="pages/edit"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <KeepoPageFormPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="editor/*"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <EditorPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="seo"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <SeoPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="analytics"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <StatisticsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="contacts"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <ContactsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="contacts/:contactId"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <ContactDetailPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="settings/*"
                      element={
                        <ProtectedRoute authenticated={authenticated}>
                          <Routes>
                            <Route
                              path="facebook-pixel"
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <FacebookPixelPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="password"
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <SitePasswordPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="age-gate"
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <SiteAgeGatePage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="google-analytics"
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <GoogleAnalytics />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="custom-domain"
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <CustomDomainPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="tag-manager"
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <TagManager />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path=""
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <SettingsPage />
                                </ProtectedRoute>
                              }
                            />{" "}
                            <Route
                              path="*"
                              element={
                                <ProtectedRoute authenticated={authenticated}>
                                  <SettingsPage />
                                </ProtectedRoute>
                              }
                            />
                          </Routes>
                        </ProtectedRoute>
                      }></Route>
                  </Routes>
                </SiteRoute>
              }
            />
            {/* Their old addresses (/keepo, /settings...): to the Keepo open, or to the list */}
            {LEGACY_SITE_ROUTES.map(route => (
              <Route key={route} path={`${route.slice(1)}/*`} element={<LegacySiteRedirect />} />
            ))}
            <Route
              path="/"
              element={
                <TailwindReset>
                  <MainComponent authenticated={authenticated} onRestore={restoreSite} />
                </TailwindReset>
              }
            />
            <Route
              path="*"
              element={
                <TailwindReset>
                  <MainComponent authenticated={authenticated} onRestore={restoreSite} />
                </TailwindReset>
              }
            />
          </Routes>
        </AppShell>
        <SaveErrorNotice />
      </Router>
    </AppContextProvider>
  );
}

export default App;
